import { useCallback, useMemo, useState } from 'react';
import type { SelectedCell } from '../components/Matrix/types.js';

const idOf = (cell: { type: string; key: string }) => `${cell.type}:${cell.key}`;

/**
 * Selection for the matrix, keyed by type AND key — a studio and a game can share a key
 * string in principle — and NOT reset when the filters change: someone narrows to drift,
 * selects a few, searches for another studio, selects a few more, and plans once.
 *
 * A selected studio carries `subtree: true`: it stands for itself and every game under it
 * in master, loaded or not, and the server expands it.
 *
 * `has`/`toggle`/`toggleAll`/`clear` are stable across renders (the setters already use
 * functional updates, so none of them needs `selected` in its closure) — a memoised row can take
 * them as props without losing its memoisation. `selectedIds` is the same membership as `has`,
 * as a `ReadonlySet` a caller can read straight off rather than calling a function per row.
 *
 * The returned object itself is memoised too — otherwise every caller gets a fresh object every
 * render regardless of how stable its properties are, and a `useCallback`/`useMemo` depending on
 * "the selection" (rather than the one function it actually calls) silently never memoises
 * anything. Depend on `selection.toggle`, not `selection`, if that is all you need.
 */
export const useSelection = () => {
    const [selected, setSelected] = useState<Map<string, SelectedCell>>(new Map());

    const toggle = useCallback((cell: SelectedCell) => {
        setSelected((current) => {
            const next = new Map(current);
            const id = idOf(cell);
            if (next.has(id)) next.delete(id);
            else next.set(id, cell);
            return next;
        });
    }, []);

    const toggleAll = useCallback((cells: SelectedCell[], select: boolean) => {
        setSelected((current) => {
            const next = new Map(current);
            for (const cell of cells) {
                if (select) next.set(idOf(cell), cell);
                else next.delete(idOf(cell));
            }
            return next;
        });
    }, []);

    const clear = useCallback(() => setSelected(new Map()), []);

    const has = useCallback((cell: { type: string; key: string }) => selected.has(idOf(cell)), [selected]);

    const selectedIds = useMemo<ReadonlySet<string>>(() => new Set(selected.keys()), [selected]);
    const cells = useMemo(() => [...selected.values()], [selected]);

    return useMemo(
        () => ({
            cells,
            count: selected.size,
            has,
            selectedIds,
            toggle,
            toggleAll,
            clear,
        }),
        [cells, selected.size, has, selectedIds, toggle, toggleAll, clear],
    );
};

export type Selection = ReturnType<typeof useSelection>;
